import { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  CircularProgress,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";
import SingleSelectionDropdown from "../../../../components/SingleSelectionDropdown"; // adjust the path if needed
import crossselect from "../../../../assets/crossselect.svg";
import Webcam from "react-webcam";
import { useRef } from "react";
import { convertLocalDateTimeToUTC } from "../../../../components/dateTimeUtils.js";
const AddPayrollDrawer = ({ open, onClose, refreshList }) => {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
const [saving, setSaving] = useState(false);
const { company } = useSelector((state) => state.auth);
const companyId = company?.id;
  const [workers, setWorkers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [openCamera, setOpenCamera] = useState(false);
const webcamRef = useRef(null);
  const startRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      worker: "",
      paymentType: "salary",
      amount: "",
      currency: "",
      date: "",
      comment: "",
      proof: null,
      attachment_url: "",
      source_expense_id: "",
    },
    validate: (values) => {
      const errors = {};
      
      // Worker (can be number or string ID)
      if (!values.worker || (typeof values.worker === "string" && values.worker.trim() === "") || values.worker === null || values.worker === undefined) {
        errors.worker = t("payrollRecords.validation.workerRequired") || "Worker is required";
      }
      
      
      // Amount
      if (!values.amount || (typeof values.amount === "string" && values.amount.trim() === "")) {
        errors.amount = t("payrollRecords.validation.amountRequired") || "Amount is required";
      } else if (isNaN(values.amount) || parseFloat(values.amount) <= 0) {
        errors.amount = t("payrollRecords.validation.amountInvalid") || "Amount must be a valid positive number";
      }
      
      // Currency
      if (!values.currency || (typeof values.currency === "string" && values.currency.trim() === "")) {
        errors.currency = t("payrollRecords.validation.currencyRequired") || "Currency is required";
      }
      
      // Date
      if (!values.date || (typeof values.date === "string" && values.date.trim() === "")) {
        errors.date = t("payrollRecords.validation.dateRequired") || "Date is required";
      }
      
      // Source Expense ID (required if payment type is expense_reimbursement)
      if (values.paymentType === "expense_reimbursement" && (!values.source_expense_id || (typeof values.source_expense_id === "string" && values.source_expense_id.trim() === "") || values.source_expense_id === null || values.source_expense_id === undefined)) {
        errors.source_expense_id = t("payrollRecords.validation.sourceExpenseRequired") || "Source expense is required for expense reimbursement";
      }
      
      return errors;
    },
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      try {
        setSaving(true);

        const payload = {
          worker_id: values.worker,
          type: values.paymentType,
          amount: parseFloat(values.amount),
          currency: values.currency,
          paid_at: convertLocalDateTimeToUTC(values.date) || values.date,
          note: values.comment,
        };

        if (values.paymentType === "expense_reimbursement") {
          payload.source_expense_id = values.source_expense_id;
        }

        if (values.attachment_url) {
          payload.attachment_url = values.attachment_url;
        }

        console.log("➡️ Final payload before sending:", payload);

        const res = await fetch(`${BASE_URL}/company-admin/expenses/remuneration`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
          body: JSON.stringify(payload),
        });

        const json = await res.json();
        console.log("✅ API response:", json);

        if (!json.error) {
          toast.success("Payment added successfully!");
          onClose();
          refreshList?.();
          formik.resetForm();
        } else {
          toast.error(json.message || "Failed to add payment");
        }
      } catch (err) {
        console.error("❌ Error saving payment:", err);
        toast.error("Error saving payment");
      } finally {
        setSaving(false);
      }
    },
  });
  
 useEffect(() => {
    if (!open) {
      formik.resetForm();  // Reset form values when the drawer is closed
    }
  }, [open]); 
  // fetch workers (no auth)
  useEffect(() => {
    if (!open) return;
    const fetchWorkers = async () => {
      try {
        setLoadingWorkers(true);
        const res = await fetch(`${BASE_URL}/public/workers?no_pagination=true&company_id=${companyId}&status=active`);
        const json = await res.json();
        if (!json.error && json.data?.records) {
          setWorkers(
            json.data.records.map((w) => ({
          label: `${w.first_name} ${w.last_name} (${w.email})`, // Show name + email
              value: w.id,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching workers:", err);
      } finally {
        setLoadingWorkers(false);
      }
    };
    fetchWorkers();
  }, [open]);

  // fetch expenses (only if reimbursement) - only approved expenses
  useEffect(() => {
    if (formik.values.paymentType !== "expense_reimbursement") return;
    const fetchExpenses = async () => {
      try {
        setLoadingExpenses(true);
        const res = await fetch(`${BASE_URL}/company-admin/expenses?status=approved`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
        });
        const json = await res.json();
        if (!json.error && json.data) {
          // Filter to only include approved expenses (double check on frontend)
          const approvedExpenses = json.data.filter((exp) => exp.status === "approved");
          setExpenses(
            approvedExpenses.map((exp) => ({
              label: `${exp.worker?.name || "Unknown"} – ${exp.description || ""}`,
              value: exp.id,
              worker_id: exp.worker?.id,
              worker_name: exp.worker?.name,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching expenses:", err);
      } finally {
        setLoadingExpenses(false);
      }
    };
    fetchExpenses();
  }, [formik.values.paymentType, decryptedToken]);

const handleFileUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const isPdf = file.type === "application/pdf";
  const formData = new FormData();
  formData.append(isPdf ? "pdf" : "image", file);

  const uploadUrl = isPdf
    ? `${BASE_URL}/upload/pdf`
    : `${BASE_URL}/upload/image`;

  try {
    const res = await fetch(uploadUrl, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    const uploadedUrl = data?.data?.url;

    formik.setFieldValue("proof", file); // ✅ store uploaded file for UI display
    formik.setFieldValue("attachment_url", uploadedUrl); // ✅ store URL for backend

    toast.success(
      data?.message || `${isPdf ? "PDF" : "Image"} uploaded successfully!`
    );
    console.log(`✅ Uploaded:`, uploadedUrl);
  } catch (err) {
    console.error("❌ Upload error:", err);
    toast.error("Error uploading file. Please try again.");
  }
};


const capturePhoto = async () => {
  const imageSrc = webcamRef.current.getScreenshot();
  if (!imageSrc) return;

  const blob = await fetch(imageSrc).then(res => res.blob());
  const file = new File([blob], `camera_${Date.now()}.jpg`, {
    type: "image/jpeg",
  });

  const fakeEvent = {
    target: {
      files: [file],
    },
  };

  setOpenCamera(false);
  handleFileUpload(fakeEvent);
};



  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E0E0E0",
          backgroundColor: "#f5f6f7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              color: "#172B4D",
            }}
          >
            {t("payrollRecords.addHeading")}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 16,
            fontWeight: 600,
            mb: 2,
            color: "#172B4D",
            textAlign: "left",
          }}
        >
          {t("payrollRecords.detailsHeading")}
        </CustomText>

        {/* Worker */}
        <Box mb={2}>
          <MultiSelectDropdown
  isSingleSelect
  label={
    <>
      {t("payrollRecords.labels.worker")} <span style={{ color: "red" }}>*</span>
    </>
  }
  name="worker"
  values={formik.values.worker ? [formik.values.worker] : []}
  onChange={({ target: { name, value } }) => {
    formik.setFieldValue(name, value.length ? value[0] : "");
    formik.setFieldTouched(name, true);
  }}
  options={workers.map((w) => ({
    label: w.label,
    value: w.value,
  }))}
  loading={loadingWorkers}
  enableSearch={true}
/>
          {formik.touched.worker && formik.errors.worker && (
            <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
              {formik.errors.worker}
            </span>
          )}
        </Box>

        {/* Amount + Currency + Date */}
        <Box display="flex" gap={2} mb={2}>
          <Box sx={{ flex: 1 }}>
            <label className="field_label">
              {t("payrollRecords.labels.amount")}{" "}
              <span className="red_asterisk">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="project_input"
              style={{
                height: "40px",
                width: "100%",
                marginTop: 6,
                border: formik.touched.amount && formik.errors.amount
                  ? "2px solid #d32f2f"
                  : "2px solid #091E4224"
              }}
            />
            {formik.touched.amount && formik.errors.amount && (
              <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {formik.errors.amount}
              </span>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
     
            <SingleSelectionDropdown
  label={
    <>
      {t("payrollRecords.labels.currency")}{" "}
      <span style={{ color: "red" }}>*</span>
    </>
  }
  name="currency"
  values={formik.values.currency ? [formik.values.currency] : []}
  onChange={({ target: { name, value } }) => {
    const selected = Array.isArray(value) ? value[0] : value;
    formik.setFieldValue(name, selected || "");
    formik.setFieldTouched(name, true);
  }}
  options={[
    { label: "USD", value: "USD" },
    // { label: "EUR", value: "EUR" },
  ]}
/>
{formik.touched.currency && formik.errors.currency && (
  <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
    {formik.errors.currency}
  </span>
)}
          </Box>

          <Box sx={{ flex: 1 }}>
            <label className="field_label">
              {t("payrollRecords.labels.date")}{" "}
              <span className="red_asterisk">*</span>
            </label>
  
            <input
             ref={startRef}
                onClick={() => startRef.current?.showPicker()} 
  type="date"
  name="date"
  className="project_input"
  style={{
    height: "40px",
    width: "100%",
    marginTop: 6,
    border: formik.touched.date && formik.errors.date
      ? "2px solid #d32f2f"
      : "2px solid #091E4224"
  }}
  value={formik.values.date}
  onChange={(e) => {
    const val = e.target.value;

    // Split into [YYYY, MM, DD]
    const parts = val.split("-");

    // Limit the year to 4 digits
    if (parts[0] && parts[0].length > 4) {
      parts[0] = parts[0].slice(0, 4);
    }

    // Join back into YYYY-MM-DD format
    const cleanVal = parts.join("-");

    // Update formik
    formik.setFieldValue("date", cleanVal);
  }}
  onBlur={formik.handleBlur}
/>
{formik.touched.date && formik.errors.date && (
  <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
    {formik.errors.date}
  </span>
)}

          </Box>
        </Box>

        {/* Comment */}
        <Box mb={2}>
          <label className="field_label">{t("payrollRecords.labels.comment")}</label>
          <textarea
            name="comment"
            rows={4}
            className="project_input"
            style={{ width: "100%", marginTop: 6,paddingTop:"5px" }}
            value={formik.values.comment}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Box>

        {/* Conditional source_expense_id */}
        {formik.values.paymentType === "expense_reimbursement" && (
          <Box mb={2}>
         
              <SingleSelectionDropdown
    label="Select Expense"
    name="source_expense_id"
    values={formik.values.source_expense_id ? [formik.values.source_expense_id] : []}
    onChange={({ target: { name, value } }) => {
      const selected = Array.isArray(value) ? value[0] : value;
      formik.setFieldValue(name, selected || "");
      formik.setFieldTouched(name, true);

      // 🔥 Auto-select worker when expense belongs to one
      const selectedExpense = expenses.find(
        (exp) => exp.value === selected
      );
      if (selectedExpense?.worker_id) {
        formik.setFieldValue("worker", selectedExpense.worker_id);
      }
    }}
    options={expenses}
  />
{formik.touched.source_expense_id && formik.errors.source_expense_id && (
  <span style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
    {formik.errors.source_expense_id}
  </span>
)}
          </Box>
        )}

     <Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 1,
  }}
>
  <CustomText
    sx={{
      fontSize: 16,
      fontWeight: 500,
      color: "#172B4D",
    }}
  >
    {t("payrollRecords.labels.supportingProof")}
  </CustomText>

  <CustomText
    sx={{
      fontSize: 15,
      fontWeight: 500,
      color: "#1976d2",
      cursor: "pointer",
         fontFamily: 'Poppins, sans-serif',
    }}
    onClick={() => setOpenCamera(true)}
  >
    {t("taskAddModal.takePhoto")}
  </CustomText>
</Box>

<Box
  sx={{
    border: "2px dashed #D0D5DD",
    borderRadius: 2,
    p: 3,
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
    mb: 0.5,
    height: 160,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    flexDirection: "column",
  }}
>
  {!formik.values.proof ? (
    <label htmlFor="proof-upload" style={{ cursor: "pointer" }}>
      <input
        type="file"
        id="proof-upload"
        name="proof"
        accept="image/*,application/pdf"
        onChange={handleFileUpload}
        hidden
      />
      <CustomText
        sx={{
          fontSize: 14,
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
          textAlign: "center",
        }}
      >
     {t("upload.label")}{" "}
        <span style={{ textDecoration: "underline", color: "#006EC2" }}>
            {t("upload.browse")}

        </span>
      </CustomText>
    </label>
  ) : (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        // border: "1px solid #E0E0E0",
        borderRadius: "8px",
        p: 1,
        // backgroundColor: "#fff",
      }}
    >
      <CustomText
        sx={{
          fontSize: 14,
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
          color: "#172B4D",
          maxWidth: "80%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {formik.values.proof.name}
      </CustomText>

      {/* Cross icon */}
      <IconButton
        size="small"
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          // background: "#F5F5F5",
          borderRadius: "50%",
          padding: "4px",
          "&:hover": { background: "#E0E0E0" },
        }}
        onClick={() => {
          formik.setFieldValue("proof", null);
          formik.setFieldValue("attachment_url", "");
        }}
      >
    ✕
      </IconButton>
    </Box>
  )}

</Box>
  <Typography sx={{
      fontSize: 13,
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
          textAlign: "left",
  }}> 
 {t("upload.note")}
  </Typography>

  
        


    

       
        <CustomButton
  label={
    saving ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} sx={{ color: "white" }} /> {/* loader */}
        {t("payrollRecords.actions.save")}
      </Box>
    ) : (
      t("payrollRecords.actions.save")
    )
  }
  sx={{ mt: 1 }}
  onClick={formik.handleSubmit}
  size="small"
  disabled={saving} 
/>
      </Box>
      {openCamera && (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      zIndex: 2000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        background: "#fff",
        borderRadius: 2,
        p: 2,
        width: 360,
      }}
    >
      <Webcam
        ref={webcamRef}
        audio={false}
        screenshotFormat="image/jpeg"
        videoConstraints={{ facingMode: "environment" }}
        style={{ width: "100%", borderRadius: 8 }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap:2,
          mt: 2,
        }}
      >
        <CustomButton
                sx={{   fontFamily: 'Poppins, sans-serif',textTransform:"capitalize"}}

          label={t("taskAddModal.camera.cancel")}
          variant="outlined"
          onClick={() => setOpenCamera(false)}
        />

        <CustomButton
                   sx={{   fontFamily: 'Poppins, sans-serif',textTransform:"capitalize"}}

          label={t("taskAddModal.camera.capture")}
          onClick={capturePhoto}
        />
      </Box>
    </Box>
  </Box>
)}

    </Drawer>
  );
};

export default AddPayrollDrawer;









